<template>
  <div v-if="show"
    class="fixed inset-0 z-[10003] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
    <div
      class="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
      <div class="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50">
        <div class="flex items-center gap-4 min-w-0">
          <h3 class="text-xl font-bold text-gray-800">Generated Barcodes</h3>
          <!-- AB#20737 — Rajco piece label (52 × 35 mm, numeric Code 128C) -->
          <div class="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 text-sm" role="radiogroup"
            aria-label="Label layout">
            <button type="button" role="radio" :aria-checked="layout === 'standard'"
              class="px-3 py-1 rounded-md transition-colors"
              :class="layout === 'standard' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'"
              @click="layout = 'standard'">Standard</button>
            <button type="button" role="radio" :aria-checked="layout === 'piece'"
              :disabled="!pieceDataAvailable"
              :title="pieceDataAvailable ? 'One 52 × 35 mm label per piece' : 'The backend has not sent piece numbers for this order yet'"
              class="px-3 py-1 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              :class="layout === 'piece' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'"
              @click="layout = 'piece'">Piece labels 52×35</button>
          </div>
        </div>
        <button @click="handleClose" class="p-2 hover:bg-gray-200 rounded-full transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24"
            stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="p-8 overflow-y-auto bg-gray-100">
        <!-- Loading State -->
        <div v-if="isLoading" class="flex items-center justify-center py-12">
          <div class="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        </div>
        <!-- AB#20737 — piece labels -->
        <template v-else-if="layout === 'piece'">
          <div v-if="hasMissingRoster"
            class="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
            Some products in this order have no roster rows, so no piece labels can be made for them.
          </div>
          <div v-if="mismatchWarnings.length > 0" data-test="piece-quantity-mismatch"
            class="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
            <p class="font-medium">The roster does not match the ordered quantity, so the label count will not match the order:</p>
            <ul class="mt-1 list-disc pl-5">
              <li v-for="warning in mismatchWarnings" :key="warning">{{ warning }}</li>
            </ul>
          </div>
          <div v-if="pieceLabels.length > 0" id="barcode-print-area" class="flex flex-wrap gap-4 justify-center">
            <div v-for="label in pieceLabels" :key="label.key" class="piece-label">
              <div class="piece-label__head">
                <span class="piece-label__order">PO {{ label.orderNo }}</span>
                <span class="piece-label__count">
                  <span v-if="label.isPair" class="piece-label__pair">PAIR</span>
                  {{ label.pieceIndex }}/{{ label.pieceTotal }}
                </span>
              </div>
              <div v-if="label.designLabel" class="piece-label__design">{{ label.designLabel }}</div>
              <div class="piece-label__product">{{ label.productName }}</div>
              <div class="piece-label__row">
                <div class="piece-label__name" :class="{
                  'piece-label__name--long': label.playerName.length > 9,
                  'piece-label__name--xlong': label.playerName.length > 14,
                }" :title="label.playerName">{{ label.playerName || '—' }}</div>
                <div class="piece-label__box">
                  <span class="piece-label__box-key">NO</span>
                  <span class="piece-label__box-val">{{ label.playerNumber || '—' }}</span>
                </div>
                <div class="piece-label__box">
                  <span class="piece-label__box-key">SIZE</span>
                  <span class="piece-label__box-val">{{ label.size || '—' }}</span>
                </div>
              </div>
              <div class="piece-label__barcode">
                <vue-barcode :value="label.barcode" :options="pieceBarcodeOptions" tag="svg" />
                <div class="piece-label__digits">{{ label.barcode }}</div>
              </div>
            </div>
          </div>
          <div v-else class="flex items-center justify-center py-12 text-gray-500">
            <p>No piece labels for the selected rows</p>
          </div>
        </template>
        <!-- Barcodes -->
        <div v-else-if="displayBarcodes.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 print:grid-cols-4" id="barcode-print-area">
          <template v-for="(barcodeItem, barcodeIdx) in displayBarcodes" :key="`${barcodeIdx}-${barcodeItem.barcode_string}`">
            <div v-for="n in barcodeItem.quantity" :key="`${barcodeIdx}-${barcodeItem.barcode_string}-${n}`"
              class="barcode-label bg-white p-1 rounded-lg shadow-sm border border-gray-200 flex flex-col items-center gap-1 break-inside-avoid print:border-0 print:shadow-none print:p-1">
              <!-- Barcode -->
                <div v-if="barcodeItem.barcode_string" class="w-full flex flex-col">
                  <div
                    class="barcode-label-meta w-full min-w-0 text-sm font-semibold leading-snug print:text-[8.2pt] print:leading-tight flex flex-col gap-y-0.5">
                    <div v-if="barcodeItem.product_name" class="min-w-0 break-words [overflow-wrap:anywhere]"
                      :title="barcodeItem.product_name">
                      ITEM: {{ barcodeItem.product_name }}
                    </div>
                    <div v-if="barcodeItem.player_name" class="min-w-0 break-words [overflow-wrap:anywhere]"
                      :title="barcodeItem.player_name">
                      PLAYER: {{ barcodeItem.player_name }}
                    </div>
                    <div
                      v-if="barcodeItem.player_number || barcodeItem.page_reference"
                      class="flex flex-wrap items-baseline justify-start gap-x-3 gap-y-0">
                      <span v-if="barcodeItem.player_number" class="shrink-0">NO: {{ barcodeItem.player_number }}</span>
                      <span v-if="barcodeItem.page_reference" class="shrink-0">PAGE: {{ barcodeItem.page_reference }}</span>
                    </div>
                    <div v-if="barcodeItem.size" class="min-w-0 mb-0.5 break-words [overflow-wrap:anywhere]"
                      :title="barcodeItem.size">
                      SIZE: {{ barcodeItem.size }}
                    </div>
                  </div>
                  <div class="barcode-svg-wrap flex w-full justify-center">
                    <vue-barcode :value="barcodeItem.ascii_barcode_string" :options="{
                      format: 'CODE128',
                      width: 1.70,
                      height: 44,
                      displayValue: true,
                      fontSize: 12,
                      margin: 0,
                      background: '#ffffff',
                      lineColor: '#000000',
                    }" tag="svg" class="max-w-full print:max-w-none" />
                  </div>
              </div>
            </div>
          </template>
        </div>
        <!-- Empty State -->
        <div v-else class="flex items-center justify-center py-12 text-gray-500">
          <p>No barcodes available</p>
        </div>
      </div>

      <div class="p-6 border-t border-gray-100 bg-white flex justify-end gap-3">
        <button @click="handleClose"
          class="px-5 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-lg transition-colors">
          Close
        </button>
        <button @click="printBarcodes" :disabled="isLoading || printableCount === 0"
          class="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
            stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          {{ layout === 'piece' ? `Print ${pieceLabels.length} piece labels` : 'Print Barcodes' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits, ref, computed, watch } from 'vue';
import VueBarcode from '@chenfengyuan/vue-barcode';
import { showToast } from '@/composables/swal';
import { OrderForBarcode, ApiResponseV1 } from '@/models';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { AxiosError } from 'axios';
import { handleResponseException } from '@/interceptors/axiosRequestInterceptor';
import {
  buildPieceLabels,
  hasPieceData,
  quantityWarnings,
  PIECE_LABEL_HEIGHT_MM,
  PIECE_LABEL_WIDTH_MM,
} from '@/utils/pieceLabels';

const props = defineProps<{
  show: boolean;
  barcodes?: OrderForBarcode['barcode_data'];
  order_no?: string;
  /** AB#20737 — pieces in the whole order, when `barcodes` is passed in. */
  piece_total?: number;
  has_missing_roster?: boolean;
  /** AB#20737 — ordered vs roster mismatch messages, when `barcodes` is passed in. */
  quantity_warnings?: string[];
}>();

const emit = defineEmits<{
  close: [];
}>();

const isLoading = ref(false);
const fetchedBarcodes = ref<OrderForBarcode['barcode_data']>([]);
const fetchedOrder = ref<
  Pick<OrderForBarcode, 'order_no' | 'piece_total' | 'has_missing_roster' | 'quantity_warnings' | 'product_quantities'> | null
>(null);
const layout = ref<'standard' | 'piece'>('standard');

// Computed property to get the barcodes to display
const displayBarcodes = computed(() => {
  // If barcodes prop is provided, use it
  if (props.barcodes && props.barcodes.length > 0) {
    return props.barcodes;
  }
  // Otherwise use fetched barcodes
  return fetchedBarcodes.value;
});

// AB#20737 — piece labels
const pieceDataAvailable = computed(() => hasPieceData(displayBarcodes.value));
const labelOrderNo = computed(
  () => fetchedOrder.value?.order_no || props.order_no || displayBarcodes.value[0]?.barcode_string || '',
);
const pieceLabels = computed(() =>
  layout.value === 'piece'
    ? buildPieceLabels(
        displayBarcodes.value,
        labelOrderNo.value,
        fetchedOrder.value?.piece_total ?? props.piece_total ?? 0,
      )
    : [],
);
const hasMissingRoster = computed(
  () => fetchedOrder.value?.has_missing_roster ?? props.has_missing_roster ?? false,
);
const mismatchWarnings = computed(() =>
  fetchedOrder.value ? quantityWarnings(fetchedOrder.value) : (props.quantity_warnings ?? []),
);
const printableCount = computed(() =>
  layout.value === 'piece' ? pieceLabels.value.length : displayBarcodes.value.length,
);
// 3 dots per module at 203 dpi; the SVG is sized in mm by CSS, and this height keeps its aspect.
const pieceBarcodeOptions = {
  format: 'CODE128C',
  width: 3,
  height: 72,
  displayValue: false,
  margin: 0,
  background: '#ffffff',
  lineColor: '#000000',
};
watch(pieceDataAvailable, (available) => {
  if (!available && layout.value === 'piece') layout.value = 'standard';
});

const fetchBarcodeData = async (orderNo: string) => {
  if (!orderNo.trim()) {
    return;
  }

  isLoading.value = true;
  fetchedBarcodes.value = [];

  try {
    const response = await http.post<ApiResponseV1<OrderForBarcode>>(`order/${orderNo}/for/barcode`);
    if (response.data.result && response.data.result.barcode_data) {
      fetchedBarcodes.value = response.data.result.barcode_data;
      fetchedOrder.value = {
        order_no: response.data.result.order_no,
        piece_total: response.data.result.piece_total,
        has_missing_roster: response.data.result.has_missing_roster,
        quantity_warnings: response.data.result.quantity_warnings,
        product_quantities: response.data.result.product_quantities,
      };
      showToast(response.data.message || 'Barcode data loaded successfully', 'success');
    } else {
      showToast('No barcode data found for this order', 'error');
    }
  } catch (error) {
    const axiosError = error as AxiosError<ApiResponseV1<null>>;
    showToast(axiosError.response?.data.message || 'Failed to fetch barcode data', 'error');
    handleResponseException(axiosError);
  } finally {
    isLoading.value = false;
  }
};

// Watch for show prop and order_no to fetch data when modal opens
watch([() => props.show, () => props.order_no], ([show, orderNo]) => {
  if (show && orderNo && !props.barcodes) {
    fetchBarcodeData(orderNo);
  } else if (show && props.barcodes) {
    // If barcodes are provided via prop, clear fetched data
    fetchedBarcodes.value = [];
  }
}, { immediate: true });



const handleClose = () => {
  emit('close');
};

const printBarcodes = () => {
  const barcodeArea = document.getElementById('barcode-print-area');
  if (!barcodeArea) {
    showToast('Barcode area not found', 'error');
    return;
  }

  // Clone the barcode area
  const printContent = barcodeArea.cloneNode(true);

  // Create a temporary container
  const tempContainer = document.createElement('div');
  tempContainer.id = 'print-container';
  tempContainer.style.cssText = `
    position: absolute;
    left: -9999px;
    top: -9999px;
    width: 100%;
  `;
  tempContainer.appendChild(printContent);
  document.body.appendChild(tempContainer);

  // AB#20737 — piece labels print on their own 52 × 35 mm page; declared after the
  // global @page below, so it wins for this print only.
  let pageStyle: HTMLStyleElement | null = null;
  if (layout.value === 'piece') {
    pageStyle = document.createElement('style');
    pageStyle.textContent = `@media print {
      @page { size: ${PIECE_LABEL_WIDTH_MM}mm ${PIECE_LABEL_HEIGHT_MM}mm; margin: 0; }
      #barcode-print-area { display: block !important; }
      .piece-label { margin: 0 !important; border: 0 !important; page-break-after: always; break-after: page; }
    }`;
    document.head.appendChild(pageStyle);
  }

  // Wait a bit for DOM to update, then print
  setTimeout(() => {
    window.print();
    // Clean up after print dialog closes
    setTimeout(() => {
      document.body.removeChild(tempContainer);
      pageStyle?.remove();
    }, 1000);
  }, 100);
};
</script>

<style>
@media print {
  @page {
    size: 50.8mm 38.1mm; /* 2" x 1.5" */
    margin: 0;
    padding: 0;
  }

  body {
    margin: 0;
    padding: 0;
  }

  body > * {
    display: none !important;
  }

  /* Only print container */
  #print-container {
    display: block !important;
    position: static !important;
    width: 100% !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  #barcode-print-area {
    display: grid !important;
    grid-template-columns: 1fr !important; /* One barcode per page */
    gap: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .barcode-label {
    width: 50.8mm !important;   /* 2.5 inch */
    height: 38.1mm !important;  /* 1.5 inch */
    padding: 2mm !important;
    margin: 0 !important;
    page-break-after: always !important;
    background: #fff !important;
    border: 0 !important;
    box-shadow: none !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
  }

  .print-barcode {
    width: 100% !important;
    height: 100% !important;
    margin: 0;
    padding: 0;
    display: flex !important;
    justify-content: center;
    align-items: center;
  }

  .barcode-svg-wrap {
    display: flex !important;
    justify-content: center !important;
    width: 100% !important;
  }

  .barcode-svg-wrap svg {
    display: block !important;
    margin-left: auto !important;
    margin-right: auto !important;
  }
  
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}

/* AB#20737 — Rajco piece label, 52 × 35 mm (same size on screen and paper) */
.piece-label {
  width: 52mm;
  height: 35mm;
  box-sizing: border-box;
  padding: 1.5mm 2mm;
  background: #fff;
  color: #000;
  border: 1px dashed #d1d5db;
  display: flex;
  flex-direction: column;
  gap: 0.5mm;
  overflow: hidden;
  font-family: Arial, Helvetica, sans-serif;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.piece-label__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 7.5pt;
  font-weight: 700;
  line-height: 1;
}
.piece-label__pair {
  border: 0.3mm solid #000;
  padding: 0 0.6mm;
  margin-right: 0.8mm;
  font-size: 6pt;
}
.piece-label__design {
  font-size: 6.5pt;
  font-weight: 700;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.piece-label__product {
  font-size: 5.5pt;
  line-height: 1.15;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}
.piece-label__row {
  display: flex;
  align-items: stretch;
  gap: 1mm;
  height: 7mm;
}
.piece-label__name {
  flex: 1;
  min-width: 0;
  align-self: center;
  font-size: 12pt;
  font-weight: 800;
  line-height: 1;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.piece-label__name--long {
  font-size: 9pt;
}
.piece-label__name--xlong {
  font-size: 7pt;
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.piece-label__box {
  border: 0.35mm solid #000;
  min-width: 9mm;
  padding: 0 0.8mm;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
.piece-label__box-key {
  font-size: 4.5pt;
  font-weight: 700;
}
.piece-label__box-val {
  font-size: 10pt;
  font-weight: 800;
  white-space: nowrap;
}
.piece-label__barcode {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.piece-label__barcode svg {
  display: block;
  /* PIECE_BARCODE_MODULES × PIECE_BARCODE_MODULE_MM = 90 × 0.375 mm (3 dots/module at 203 dpi) */
  width: 33.75mm;
  height: 9mm;
}
.piece-label__digits {
  font-size: 7pt;
  font-weight: 700;
  letter-spacing: 0.4mm;
  line-height: 1.1;
}

/* Screen styles */
.barcode-label {
  transition: transform 0.2s;
}

.barcode-label:hover {
  transform: scale(1.02);
}
</style>

